import { Mountain, User, Route, Calendar, Map, Star, Info, Home, Phone, Mail, Globe, Music, Camera, ShoppingBag, Utensils, Trees, Building, Bike, Fish, Sunset, type LucideIcon } from "lucide-react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { WeatherWidget } from "@/components/WeatherWidget";
import { HeroSection } from "@/components/HeroSection";
import { NavigationCard } from "@/components/NavigationCard";
import { FeatureImage } from "@/components/FeatureImage";
import { ActionButton } from "@/components/ActionButton";
import { WebViewModal } from "@/components/WebViewModal";
import { useURLConfig } from "@/hooks/useURLConfig";
import { HealthCheck } from "@/components/HealthCheck";
import { usePrefetch } from "@/hooks/usePrefetch";
import { StatusBar } from "@/components/StatusBar";
import type { NavButtonIcon } from "@/components/MunicipalityForm";
import { isUrlBlocked } from "@/lib/blockedDomains";

const ICON_MAP: Record<NavButtonIcon | string, LucideIcon> = {
  Mountain, Route, User, Calendar, Map, Star, Info,
  Home, Phone, Mail, Globe, Music, Camera, ShoppingBag,
  Utensils, Trees, Building, Bike, Fish, Sunset,
};

// Extrae el dominio de una URL de forma segura
const extractDomain = (url: string): string | null => {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
};

// Valida si una URL está en la lista de dominios permitidos
const isUrlAllowed = (url: string, allowedDomains: string[]): boolean => {
  try {
    const domain = new URL(url).hostname;
    return allowedDomains.some(allowed =>
      domain === allowed || domain.endsWith('.' + allowed)
    );
  } catch {
    return false;
  }
};

const Index = () => {
  const config = useURLConfig();
  const navigate = useNavigate();
  const hasCustomButtons = useMemo(() => {
    return (config.navButtons && config.navButtons.length > 0) ||
      (config.bottomButtons && config.bottomButtons.length > 0);
  }, [config.navButtons, config.bottomButtons]);

  // La lista blanca contiene exclusivamente destinos visibles/configurados en
  // el tótem. Los recursos incrustados pueden venir de otros dominios, pero la
  // navegación principal nunca podrá salir de esta lista.
  const allowedDomains = useMemo(() => {
    const domains = new Set<string>();
    const addUrl = (candidate?: string) => {
      if (!candidate) return;
      const domain = extractDomain(candidate);
      if (domain) domains.add(domain.toLowerCase().replace(/^www\./, ''));
    };

    config.heroes.forEach(hero => {
      addUrl(hero.clickUrl);
    });

    if (hasCustomButtons) {
      [...(config.navButtons ?? []), ...(config.bottomButtons ?? [])].forEach(btn => {
        if (btn.action === 'external_url') addUrl(btn.url);
      });
    } else {
      addUrl(config.enjoyUrl);
      addUrl(config.carpetaniaUrl);
      addUrl('https://sierranortemadrid.mystreetbook.es/');
      addUrl('https://totems.sierranortemadrid.org/events');
      addUrl(config.newsletterUrl || 'https://totems.sierranortemadrid.org/newsletter');
    }

    return Array.from(domains);
  }, [config, hasCustomButtons]);

  const [webViewState, setWebViewState] = useState<{
    isOpen: boolean;
    url: string;
    title?: string;
  }>({
    isOpen: false,
    url: '',
    title: undefined
  });

  // usePrefetch({
  //   urls: [
  //     'https://totems.sierranortemadrid.org/enjoy',
  //     config.carpetaniaUrl,
  //     'https://totems.sierranortemadrid.org/events',
  //     'https://totems.sierranortemadrid.org/newsletter',
  //   ],
  //   delay: 3000,
  //   enabled: true
  // });

  useEffect(() => {
    const handleCloseWebView = () => {
      setWebViewState({ isOpen: false, url: '', title: undefined });
    };

    window.addEventListener('closeWebView', handleCloseWebView);

    return () => {
      window.removeEventListener('closeWebView', handleCloseWebView);
    };
  }, []);

  const openWebView = (url: string, title?: string) => {
    if (isUrlBlocked(url, config.proxyBlockedDomains)) {
      return;
    }

    // Validar si la URL está permitida
    if (!isUrlAllowed(url, allowedDomains)) {
      console.log('⛔ URL no permitida, ignorando:', url);
      return; // No hacer nada, quedarse donde está
    }
    setWebViewState({ isOpen: true, url, title });
    window.dispatchEvent(new Event('webViewOpen'));
  };

  const closeWebView = useCallback(() => {
    setWebViewState({ isOpen: false, url: '', title: undefined });
    window.dispatchEvent(new Event('webViewClose'));
  }, []);

  const showBottomButtons = !hasCustomButtons || config.bottomButtons.length > 0;

  return (
    <div className="page-wrapper">
      <HealthCheck />
      <StatusBar />
      <WeatherWidget
        location={config.city}
        lat={config.lat}
        lon={config.lon}
        apiKey={config.apiKey}
      />

      <div className="container">
        {config.heroes && config.heroes.length > 0 && (
          <HeroSection
            heroes={config.heroes.map(hero => ({
              name: hero.name,
              image: hero.imageUrl,
              onClick: () => openWebView(hero.clickUrl, hero.name)
            }))}
          />
        )}

        {(!hasCustomButtons || (config.navButtons && config.navButtons.length > 0)) && (
          <div className="buttons-grid-top">
            {config.navButtons && config.navButtons.length > 0 ? (
              config.navButtons.map((btn) => {
                const IconComponent = ICON_MAP[btn.icon] ?? Mountain;
                const handleClick = () => {
                  if (btn.action === 'internal_page' && btn.page) {
                    navigate(`${btn.page}${window.location.search}`);
                  } else if (btn.action === 'external_url' && btn.url) {
                    openWebView(btn.url, btn.label);
                  }
                };
                return (
                  <NavigationCard
                    key={btn.id}
                    icon={IconComponent}
                    title={btn.label}
                    imageUrl={btn.imageUrl}
                    onClick={handleClick}
                  />
                );
              })
            ) : (
              <>
                <NavigationCard
                  icon={Mountain}
                  title="Disfruta la lugo"
                  onClick={() => openWebView(config.enjoyUrl, "Disfruta la lugo")}
                />
                <NavigationCard
                  icon={Route}
                  title="Red de Caminos Carpetania"
                  onClick={() => openWebView(config.carpetaniaUrl, "Red de Caminos Carpetania")}
                />
                <NavigationCard
                  icon={User}
                  title="Recorre el municipio"
                  onClick={() => openWebView("https://sierranortemadrid.mystreetbook.es/", "Recorre el municipio")}
                />
                <NavigationCard
                  icon={Calendar}
                  title="Agenda"
                  onClick={() => openWebView("https://totems.sierranortemadrid.org/events", "Agenda")}
                />
              </>
            )}
          </div>
        )}

        <FeatureImage
          municipalityId={config.municipalityId}
          randomOrder={config.carouselRandomOrder}
          alt={`Vista histórica de ${config.municipality}`}
        />

        {showBottomButtons && (
          <div className="buttons-grid-bottom">
            {config.bottomButtons && config.bottomButtons.length > 0 ? (
              config.bottomButtons.map((btn) => {
                const handleClick = () => {
                  if (btn.action === 'internal_page' && btn.page) {
                    navigate(`${btn.page}${window.location.search}`);
                  } else if (btn.action === 'external_url' && btn.url) {
                    openWebView(btn.url, btn.label);
                  }
                };
                return (
                  <ActionButton key={btn.id} imageUrl={btn.imageUrl} onClick={handleClick}>
                    {btn.label}
                  </ActionButton>
                );
              })
            ) : (
              <>
                <ActionButton onClick={() => openWebView(config.newsletterUrl || "https://totems.sierranortemadrid.org/newsletter", "Boletín")}>
                  Accede a la web del municipio
                </ActionButton>
                <ActionButton onClick={() => openWebView(config.newsletterUrl || "https://totems.sierranortemadrid.org/newsletter", "Boletín")}>
                  ¿Quieres recibir nuestra agenda de actividades?
                </ActionButton>
              </>
            )}
          </div>
        )}
        {!showBottomButtons && <div className="page-bottom-spacing" aria-hidden="true" />}
      </div>

      <WebViewModal
        isOpen={webViewState.isOpen}
        onClose={closeWebView}
        url={webViewState.url}
        title={webViewState.title}
        blockedDomains={config.proxyBlockedDomains}
        allowedDomains={allowedDomains}
      />
    </div>
  );
};
export default Index;
